import type { AnchorHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button, Icon, Stack, tokens } from '@hotjar/hotsauce';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { epochDateTime, timeAgo } from '@hotjar/webapp/lib/time';
import type { VotingAction } from '@hotjar/feedback/domain/common/Responses/VotingAction';
import type { FeedbackResponseDto } from '@hotjar/feedback/domain/IncomingFeedback/FeedbackResponseDto';
import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import { EmotionIcon } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import { getBrowserIcon, getDeviceIcon, getSystemIcon } from '@hotjar/webapp/utils/icons';
import { stripDomain } from '@hotjar/webapp/utils/urls';
import { getLocalizedCountryNameFromCode } from '@hotjar/country-lib';
import type { CountryCode } from '@hotjar/country-lib';
import { ResponseFeedbackTooltip } from '@hotjar/webapp/features/Feedback/Responses/ResponseFeedbackTooltip';
import { CommentsContext } from '@hotjar/webapp/contexts/comments';
import { useTranslation } from 'react-i18next';
import { useOperatingSystemName, useBrowserName } from '@hotjar/feedback';
import { Screenshot } from './components/Screenshot';
import { ResponseComments } from '../../../common/Comments/FeedbackResponse';
import type { ResponseCommentsProps } from '../../../common/Comments/FeedbackResponse';

// TODO: Could be a token
const ellipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// TODO: Could be a token
const placeholder = css`
  font-style: italic;
  color: ${tokens.color.text_disabled};
`;

const Wrapper = styled.div`
  display: flex;
  position: relative;
  background-color: ${tokens.color.ui_background};
  gap: ${tokens.spacer.x2};
`;

const Content = styled.div`
  flex-grow: 1;
  flex-direction: column;
  display: flex;
  gap: ${tokens.spacer.x2};
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  ${tokens.text('body1')}
`;

const HeaderEmotion = styled.div`
  flex-shrink: 0;
`;

const Message = styled.span<{ isPlaceholder?: boolean }>`
  display: inline-block;
  margin-left: ${tokens.spacer.x2};
  ${(props) => props.isPlaceholder === true && placeholder}
`;

const Quote = styled.strong`
  display: inline-block;
`;

const Details = styled.div`
  width: 250px;
`;

const Subtitle = styled.div`
  ${tokens.text('subtitle2')}
  color: ${tokens.color.text_default};
  margin: 0 0 ${tokens.spacer.x1};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 ${tokens.spacer.x3};
`;

const DetailsItem = styled.li`
  display: flex;
  align-items: center;
  margin: 0 0 ${tokens.spacer.x1};
`;

const DetailsIcon = styled.div`
  flex-shrink: 0;
  display: flex;
`;

const DetailsText = styled.div<{ isPlaceholder?: boolean; capitalize?: boolean }>`
  padding: 0 ${tokens.spacer.x1};
  ${tokens.text('body2')}
  ${ellipsis}

  ${(props) => props.capitalize === true && `text-transform: capitalize;`}
  color: ${tokens.color.text_light};

  &,
  &:hover,
  &:focus,
  &:active {
    color: ${tokens.color.text_light};
    ${(props) => props.isPlaceholder === true && placeholder}
  }
`;

const DetailsLink = styled(DetailsText)<AnchorHTMLAttributes<HTMLAnchorElement>>``;

const PlaceholderText = styled.div`
  ${tokens.text('body2')}
  ${placeholder}
  padding: 0 0 ${tokens.spacer.x3};
`;

const Comments = styled(ResponseComments)`
  padding: ${tokens.spacer.x2} 0 0 0;
`;

const FeedbackButton = styled.div`
  display: inline-flex;
  width: ${tokens.spacer.x4};
  height: ${tokens.spacer.x4};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${tokens.color.secondary_cta};
  border: 1px solid ${tokens.color.secondary_cta};
  border-radius: ${tokens.radius.m};
`;

function isAnonymousUser(userId = ''): boolean {
  return !userId || userId.substring(0, 8) === '00000000';
}

export interface ResponseModalContentProps {
  siteId: number;
  feedbackId: number;
  response: FeedbackResponseDto;
  emotionStyle: EmotionStyle;
  commentsProps: ResponseCommentsProps;
  canDelete: boolean;
  onDelete: () => void;
  onFeedbackChange: (vote: VotingAction, responseId: FeedbackResponseDto['id']) => void;
}

export function ResponseModalContent(props: ResponseModalContentProps): JSX.Element {
  const {
    response,
    emotionStyle,
    commentsProps,
    siteId,
    feedbackId,
    onFeedbackChange,
    canDelete,
    onDelete,
  } = props;

  const hasMessage =
    response.content.message !== undefined && response.content.message.trim().length > 0;
  const isAnonymous = isAnonymousUser(response.short_visitor_uuid);
  const hasUser =
    !isAnonymous || response.content.email !== undefined || response.country_name !== null;

  const { t } = useTranslation('feedback');
  const browserName = useBrowserName(response.browser);
  const osName = useOperatingSystemName(response.os);

  return (
    <Wrapper>
      <Content>
        <Header>
          <HeaderEmotion>
            <EmotionIcon
              emotionStyle={emotionStyle}
              score={response.content.emotion}
              size={emotionStyle === 'star' ? 's' : 'm'}
            />
          </HeaderEmotion>
          {hasMessage && (
            <Message>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <Quote>“</Quote> {response.content.message} <Quote>”</Quote>
            </Message>
          )}
          {!hasMessage && (
            <Message isPlaceholder>{t('feedback-response-modal.no-feedback')}</Message>
          )}
        </Header>
        <Screenshot src={response.image_url} increaseHeight />
      </Content>
      <Details>
        <Subtitle>{t('feedback-response-modal.response-title')}</Subtitle>
        <List>
          <DetailsItem>
            <DetailsIcon>
              <Icon icon="hashtag" />
            </DetailsIcon>
            <Tooltip content={t('feedback-response-modal.tooltip.response-id')}>
              <DetailsText>{response.id}</DetailsText>
            </Tooltip>
          </DetailsItem>
          <DetailsItem>
            <DetailsIcon>
              <Icon icon="navigate" />
            </DetailsIcon>
            <Tooltip content={response.response_url}>
              <DetailsLink
                as="a"
                href={response.response_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {stripDomain(response.response_url)}
              </DetailsLink>
            </Tooltip>
          </DetailsItem>
        </List>
        <Subtitle>{t('feedback-response-modal.user-title')}</Subtitle>
        {hasUser ? (
          <List>
            <DetailsItem>
              <DetailsIcon>
                <Icon icon="user_single" />
              </DetailsIcon>
              <Tooltip content={t('feedback-response-modal.tooltip.hotjar-user-id')}>
                <DetailsText isPlaceholder={isAnonymous}>
                  {isAnonymous
                    ? t('feedback-response-modal.anonymous')
                    : response.short_visitor_uuid}
                </DetailsText>
              </Tooltip>
            </DetailsItem>
            <DetailsItem>
              <DetailsIcon>
                <span className={`flag flag-${response.country_code ?? ''}`} />
              </DetailsIcon>
              <DetailsText isPlaceholder={response.country_name === null}>
                {response.country_code !== null
                  ? getLocalizedCountryNameFromCode(
                      response.country_code.toUpperCase() as CountryCode,
                    )
                  : t('feedback-response-modal.unknown-country')}
              </DetailsText>
            </DetailsItem>
            <DetailsItem>
              <DetailsIcon>
                <Icon icon="email" />
              </DetailsIcon>
              <Tooltip
                enabled={response.content.email !== undefined}
                content={response.content.email ?? ''}
              >
                <DetailsText isPlaceholder={response.content.email === undefined}>
                  {response.content.email ?? t('feedback-response-modal.left-blank')}
                </DetailsText>
              </Tooltip>
            </DetailsItem>
          </List>
        ) : (
          <PlaceholderText>{t('feedback-response-modal.no-details')}</PlaceholderText>
        )}
        <Subtitle>{t('feedback-response-modal.session-title')}</Subtitle>
        <List>
          <DetailsItem>
            <DetailsIcon>
              <Icon icon="time" />
            </DetailsIcon>
            <Tooltip content={epochDateTime(response.created_epoch_time)}>
              <DetailsText>{timeAgo(response.created_epoch_time, null)}</DetailsText>
            </Tooltip>
          </DetailsItem>
          <DetailsItem>
            <DetailsIcon>
              <Icon icon={getDeviceIcon(response.device)} />
            </DetailsIcon>
            <DetailsText capitalize>
              {t('feedback-card.device.device-details', {
                x: response.window_size[0],
                y: response.window_size[1],
                device: response.device,
              })}
            </DetailsText>
          </DetailsItem>
          <DetailsItem>
            <DetailsIcon>
              <Icon icon={getBrowserIcon(response.browser)} />
            </DetailsIcon>
            <Tooltip content={t('feedback-response-modal.tooltip.browser')}>
              <DetailsText>{browserName}</DetailsText>
            </Tooltip>
          </DetailsItem>
          <DetailsItem>
            <DetailsIcon>
              <Icon icon={getSystemIcon(response.os)} />
            </DetailsIcon>
            <Tooltip content={t('feedback-response-modal.tooltip.system')}>
              <DetailsText>{osName}</DetailsText>
            </Tooltip>
          </DetailsItem>
        </List>
        <Stack direction="row" spacing="x1">
          <ResponseFeedbackTooltip
            votingAction={response.voting_action}
            siteId={siteId}
            feedbackId={feedbackId}
            responseId={response.id}
            onChange={onFeedbackChange}
            renderReference={({ renderDefaultReference }) => (
              <FeedbackButton>{renderDefaultReference()}</FeedbackButton>
            )}
            color={tokens.color.secondary_cta}
          />
          <Button size="s" kind="secondary" icon="trash" isDisabled={!canDelete} onClick={onDelete}>
            {t('feedback-response-modal.action.delete')}
          </Button>
        </Stack>

        <CommentsContext>
          <Comments {...commentsProps} />
        </CommentsContext>
      </Details>
    </Wrapper>
  );
}
